import React, { useState } from 'react';
import { DocumentNode } from 'graphql';
import { Widget } from '@spins/react-ui-library';
import OpportunityMatrixTable from 'components/OpportunityMatrixTable';
import { SortDirection, GlobalFilterType } from 'utils/types';
import Paginator from 'components/Paginator';
import QueryRetryWrapper from 'components/QueryRetryWrapper';
import { isEmpty } from 'utils/functions';
import { Alert } from 'react-bootstrap';

interface Props {
  clientId: string;
  dataTotalResolver(data: any): number;
  defaultSortBy: string;
  defaultSortDirection?: string;
  globalFilter: GlobalFilterType;
  matrixTableDataResolver(
    data: any,
    currentSortBy: string,
    currentSortDirection: string,
    sortHandler: any
  ): any;
  query: DocumentNode;
  title: string;
  dataResolver(data: any): any;
}

const OpportunityMatrixWidget: React.FC<Props> = ({
  clientId,
  dataTotalResolver,
  defaultSortBy,
  defaultSortDirection = SortDirection.ASC,
  globalFilter,
  matrixTableDataResolver,
  query,
  title,
  dataResolver
}) => {
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSortDirection, setCurrentSortDirection] = useState(defaultSortDirection);
  const [currentSortBy, setCurrentSortBy] = useState(defaultSortBy);

  const getPageable = (page, sortedBy, sortDirection) => ({
    page,
    pageSize: 10,
    sortedBy,
    sortDirection
  });

  const handleSort = (refetch, sortBy: string) => {
    let newSortDirection;
    if (sortBy === currentSortBy) {
      newSortDirection = SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC;
    } else {
      newSortDirection = SortDirection.ASC;
    }
    setCurrentSortDirection(newSortDirection);
    setCurrentSortBy(sortBy);
    refetch({
      pageable: getPageable(currentPage, sortBy, newSortDirection)
    });
  };

  return (
    <Widget>
      {{
        header: {
          title: `${title} (${total})`
        },
        body: {
          content: (
            <QueryRetryWrapper
              query={query}
              variables={{
                clientID: clientId,
                globalFilter,
                pageable: getPageable(currentPage, currentSortBy, currentSortDirection)
              }}
              fetchPolicy="cache-first"
            >
              {({ data, refetch }) => {
                if (isEmpty(dataResolver(data)))
                  return <Alert variant="warning">No Data Found</Alert>;
                setTotal(dataTotalResolver(data));
                return (
                  <React.Fragment>
                    <OpportunityMatrixTable
                      {...matrixTableDataResolver(
                        data,
                        currentSortBy,
                        currentSortDirection,
                        (sortBy: string) => handleSort(refetch, sortBy)
                      )}
                    />
                    <div className="paginator-container">
                      <Paginator
                        currentPage={currentPage}
                        total={total}
                        onNextClick={nextPage => {
                          setCurrentPage(nextPage);
                          refetch({
                            pageable: getPageable(nextPage, currentSortBy, currentSortDirection)
                          });
                        }}
                        onPreviousClick={previousPage => {
                          setCurrentPage(previousPage);
                          refetch({
                            pageable: getPageable(previousPage, currentSortBy, currentSortDirection)
                          });
                        }}
                      />
                    </div>
                  </React.Fragment>
                );
              }}
            </QueryRetryWrapper>
          )
        }
      }}
    </Widget>
  );
};

export default OpportunityMatrixWidget;
